import {
  ViewFilterSchema,
  ViewFilterSchemaParams,
  TablesListParams,
  ViewCompact,
  SchemaViewsParams,
  ViewsListCompactParams,
  ViewCompactCluster,
  ViewCompactSchema
} from './viewsSlice'
import type { DsView, ViewsListCountParams, ViewsListParams } from './viewsType'
import {
  FILTER_PII_VALUES,
  GRAPHQL_API_FILTERS,
  GRAPHQL_CLASSIFICATION_TYPE,
  LIMIT_DEFAULT,
  ON_DEMAND_SCAN_STATUS,
  PAGE
} from '../../constants'
import { getAfterCursor } from '../../utils/graphqlUtil'
import type { GqlConnection } from '../../utils/types'
import { gql } from 'graphql-request'

const mapPiiDetectionFilterToQueryV2 = (
  params: Pick<TablesListParams, 'isSensitive' | 'piiDetection'>
) => {
  const { isSensitive, piiDetection: filter = [] } = params

  if (isSensitive && !filter.length) {
    return { toggleFilter: `{ key: ${GRAPHQL_API_FILTERS.isPiiOrMaybePii} }` }
  }

  if (
    filter.includes(FILTER_PII_VALUES.pii) &&
    filter.includes(FILTER_PII_VALUES.maybePii) &&
    filter.includes(FILTER_PII_VALUES.nonPii)
  ) {
    return {}
  }

  if (filter.includes(FILTER_PII_VALUES.pii) && filter.includes(FILTER_PII_VALUES.maybePii)) {
    return { toggleFilter: `{ key: ${GRAPHQL_API_FILTERS.isPiiOrMaybePii} }` }
  }

  // TODO: this case is broken because lack of API
  if (filter.includes(FILTER_PII_VALUES.pii) && filter.includes(FILTER_PII_VALUES.nonPii)) {
    return { classification: GRAPHQL_CLASSIFICATION_TYPE.NOT_PII }
  }

  if (filter.includes(FILTER_PII_VALUES.maybePii) && filter.includes(FILTER_PII_VALUES.nonPii)) {
    return {
      toggleFilter: `{ key: ${GRAPHQL_API_FILTERS.isMaybePII} }`,
      classification: GRAPHQL_CLASSIFICATION_TYPE.NOT_PII
    }
  }

  if (filter.includes(FILTER_PII_VALUES.pii)) {
    return { classification: GRAPHQL_CLASSIFICATION_TYPE.PII }
  }

  if (filter.includes(FILTER_PII_VALUES.maybePii)) {
    return { toggleFilter: `{ key: ${GRAPHQL_API_FILTERS.isMaybePII} }` }
  }

  if (filter.includes(FILTER_PII_VALUES.nonPii)) {
    return { classification: GRAPHQL_CLASSIFICATION_TYPE.NOT_PII }
  }

  return {}
}

export const queryViews = (params: ViewsListParams) => {
  const { pageSize = LIMIT_DEFAULT, page = 1, searchQuery, filters, isAtRisk } = params

  const cursor = getAfterCursor(page, pageSize)

  const { classification, toggleFilter } = mapPiiDetectionFilterToQueryV2(params)

  const booleanFilterMap = new Map([[GRAPHQL_API_FILTERS.isView, true]])

  if (isAtRisk) {
    booleanFilterMap.set(GRAPHQL_API_FILTERS.isReviewed, false)
  }

  const booleanFilter = [...booleanFilterMap.entries()].map(
    ([key, value]) => `{ key: ${key}, value: ${value} }`
  )

  const filterEntries = Object.entries(filters).map(
    ([key, values]) => `{ key: ${key}, values: ${JSON.stringify(values)} }`
  )

  return gql`
    {
      datasources (
        id: "${params.datasourceId}"
      ) {
        edges {
          node {
            tables(
              first: ${pageSize}
              after: "${cursor}"
              ${searchQuery ? `query: "${searchQuery}"` : ''}
              ${toggleFilter ? `toggleFilter: ${toggleFilter}` : ''}
              ${classification ? `classification: ${classification}` : ''}
              booleanFilter: [${booleanFilter.join(', ')}]
              filter: [${filterEntries.join(', ')}]
            ) {
              edges {
                node {
                  id
                  name
                  rowCount
                  isView
                  isReviewed
                  schema {
                    edges {
                      node {
                        name
                      }
                    }
                  }
                  database {
                    edges {
                      node {
                        name
                      }
                    }
                  }
                  columnsSensitiveCount: columns(classification: ${
                    GRAPHQL_CLASSIFICATION_TYPE.PII
                  }) {
                    count
                  }
                  columns {
                    count
                  }
                  onDemandScanStatus
                }
              }
            }
          }
        }
      }
    }
  `
}

type QueryViewsResponse = {
  datasources?: GqlConnection<{
    tables: GqlConnection<{
      id: string
      name: string
      isView: boolean
      schema: GqlConnection<{ name: string }>
      database: GqlConnection<{ name: string }>
      columnsSensitiveCount: { count: number }
      columns: { count: number }
      rowCount: number
      isReviewed: boolean
      onDemandScanStatus: ON_DEMAND_SCAN_STATUS
    }>
  }>
}

export const mapQueryViews = (response: QueryViewsResponse): DsView[] => {
  return (response?.datasources?.edges
    ?.flatMap((edge) =>
      edge.node?.tables?.edges?.map(({ node }) => {
        const dsView: DsView = {
          ...node,
          viewId: node.id,
          schema: node?.schema?.edges?.[0].node?.name,
          database: node?.database?.edges?.[0].node?.name,
          columnsSensitiveCount: node.columnsSensitiveCount.count,
          columnsCount: node.columns.count,
          rowsCount: node.rowCount,
          isReviewed: node.isReviewed
        }

        return dsView
      })
    )
    .filter(Boolean) ?? []) as DsView[]
}

export const queryViewsListCount = (params: ViewsListCountParams): string => {
  const { searchQuery, filters } = params

  const { classification, toggleFilter } = mapPiiDetectionFilterToQueryV2({
    ...params,
    isSensitive: false
  })
  const {
    classification: piiViewsClassification,
    toggleFilter: piiViewsToggleFilter
  } = mapPiiDetectionFilterToQueryV2({
    ...params,
    isSensitive: true
  })

  const booleanFilterMap = new Map([[GRAPHQL_API_FILTERS.isView, true]])

  const booleanFilter = [...booleanFilterMap.entries()].map(
    ([key, value]) => `{ key: ${key}, value: ${value} }`
  )

  const filterEntries = Object.entries(filters).map(
    ([key, values]) => `{ key: ${key}, values: ${JSON.stringify(values)} }`
  )

  return gql`
    {
      datasources (
        id: "${params.datasourceId}",
      )  {
        edges {
          node {
            tables (
              ${searchQuery ? `query: "${searchQuery}"` : ''}
              ${classification ? `classification: ${classification}` : ''}
              ${toggleFilter ? `toggleFilter: ${toggleFilter}` : ''}
              booleanFilter: [${booleanFilter.join(', ')}]
              filter: [${filterEntries.join(', ')}]
            ) {
              count
            }
            tablesPii: tables (
              ${searchQuery ? `query: "${searchQuery}"` : ''}
              ${piiViewsToggleFilter ? `toggleFilter: ${piiViewsToggleFilter}` : ''}
              ${piiViewsClassification ? `classification: ${piiViewsClassification}` : ''}
              booleanFilter: [${booleanFilter.join(', ')}]
              filter: [${filterEntries.join(', ')}]
            ) {
              count
            }
            tablesNeedReview: tables (
              ${searchQuery ? `query: "${searchQuery}"` : ''}
              ${classification ? `classification: ${classification}` : ''}
              ${toggleFilter ? `toggleFilter: ${toggleFilter}` : ''}
              booleanFilter: [
                ${booleanFilter.join(', ')}
                { key: ${GRAPHQL_API_FILTERS.isReviewed}, value: false }
              ]
              filter: [${filterEntries.join(', ')}]
            ) {
              count
            }
          }
        }
      }
    }
  `
}

type QueryViewsListCountResponse = {
  datasources?: {
    edges?: Array<{
      node?: {
        tables?: { count?: number }
        tablesPii?: { count?: number }
        tablesNeedReview?: { count?: number }
      }
    }>
  }
}
export const mapQueryViewsListCount = (
  response: QueryViewsListCountResponse
): {
  tablesCount: number
  tablesPiiCount: number
  tablesNeedReviewCount: number
} => {
  const tablesCount = response?.datasources?.edges?.[0]?.node?.tables?.count ?? 0
  const tablesPiiCount = response?.datasources?.edges?.[0]?.node?.tablesPii?.count ?? 0
  const tablesNeedReviewCount =
    response?.datasources?.edges?.[0]?.node?.tablesNeedReview?.count ?? 0

  return {
    tablesCount,
    tablesPiiCount,
    tablesNeedReviewCount
  }
}

export const MAX_SCHEMA_VIEWS_COUNT = 25
export const querySchemaViews = (params: SchemaViewsParams): string => {
  const cursor = getAfterCursor(params[PAGE], MAX_SCHEMA_VIEWS_COUNT)

  return `
  {
    datasources(first: 1, id: "${params.datasourceId}") {
      edges {
        node {
          schemas(first:1, id:"${params.schemaId}") {
            edges {
              node {
                tables(
                  first: ${MAX_SCHEMA_VIEWS_COUNT},
                  after: "${cursor}"
                  booleanFilter: [{ key: ${GRAPHQL_API_FILTERS.isView}, value: true }]
                ) {
                  count
                  edges {
                    node {
                      id
                      name
                      rowCount
                      isReviewed
                      changeType
                      clusterRootTableUrn
                      columns {
                        count
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }`
}

export const mapQuerySchemaViews = (raw: any) => {
  try {
    const views =
      raw.datasources.edges[0].node?.schemas?.edges[0]?.node?.tables?.edges?.map(({ node }) => {
        const view: ViewCompact = {
          tableId: decodeURIComponent(node.id),
          tableName: node.name,
          changeType: node.changeType,
          rowsCount: node.rowCount || 0,
          columnsCount: node.columns?.count || 0,
          parentId: decodeURIComponent(node.clusterRootTableUrn)
        }

        return view
      }) || []

    return {
      total: raw.datasources?.edges[0].node?.schemas?.edges[0]?.node?.tables?.count || 0,
      views
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    throw error
  }
}

export const queryViewCompactClusters = (params: ViewsListCompactParams): string => {
  return gql`
    {
      datasources(id: "${params.datasourceId}") {
        edges {
          node {
            tables(classification: ${GRAPHQL_CLASSIFICATION_TYPE.IDENTITY}) {
              count
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  `
}

export const queryViewCompactSchemas = (params: ViewsListCompactParams): string => {
  return gql`
    {
      datasources(id: "${params.datasourceId}") {
        edges {
          node {
            schemas {
              count
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryViewCompactSchemas = (
  raw: any
): { total: number; schemas: ViewCompactSchema[] } => {
  try {
    const schemas = raw.datasources?.edges[0]?.node?.schemas?.edges?.map(({ node: schema }) => ({
      schemaId: decodeURIComponent(schema.id),
      schemaName: schema.name
    }))

    return { total: raw.datasources?.edges[0]?.node?.schemas?.count || 0, schemas }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    throw error
  }
}

export const mapQueryViewCompactClusters = (
  raw: any
): { total: number; clusters: ViewCompactCluster[] } => {
  try {
    const clusters = raw.datasources?.edges[0]?.node?.tables?.edges?.map(({ node: table }) => ({
      clusterId: decodeURIComponent(table.id),
      clusterName: table.name
    }))

    return { total: raw.datasources?.edges[0]?.node?.tables?.count || 0, clusters }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    throw error
  }
}

export const mutationReviewViews = (viewIds: string[]): string => {
  return gql`
    mutation {
      reviewTables(
        tableIds: ${JSON.stringify(viewIds)}
      ) {
        success
        message
      }
    }
  `
}

export const queryTableFilterAttributes = (): string => {
  return gql`
    {
      attribute {
        edges {
          node {
            id
            name
            internalName
            attributeSets {
              edges {
                node {
                  id
                  name
                  enabled
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryTableFilterAttributes = (raw: any) => {
  try {
    return raw.attribute?.edges?.map(({ node: attr }) => ({
      id: attr.id,
      name: attr.name,
      internalName: attr.internalName,
      attributeSets: attr.attributeSets?.edges?.map(({ node: set }) => ({
        id: set.id,
        name: set.name,
        enabled: set.enabled
      }))
    }))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    throw error
  }
}

export const queryTableFilterSchemas = (params: ViewFilterSchemaParams): string => {
  return gql`
    {
      datasources(id: "${params.datasourceId}") {
        edges {
          node {
            schemas {
              edges {
                node {
                  id
                  name
                  database {
                    edges {
                      node {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryTableFilterSchemas = (raw: any): ViewFilterSchema[] => {
  try {
    return raw.datasources?.edges[0]?.node?.schemas?.edges?.map(({ node: schema }) => ({
      id: schema.id,
      name: schema.name,
      databases: schema.database?.edges?.map(({ node: db }) => ({
        id: db.id,
        name: db.name
      }))
    }))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    throw error
  }
}

export const triggerViewOnDemandScan = (viewId: string) => {
  return gql`
    mutation {
      triggerTableOnDemandScan(tableId: "${viewId}"){
        success
      }
    }
  `
}
